import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { 
  Users, 
  Plus, 
  Edit,
  Trash2,
  Search,
  UserPlus,
  X,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import GroupTaskModal from './GroupTaskModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserGroups = ({ onTaskCreated }) => {
  const { token, user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGroupTaskModal, setShowGroupTaskModal] = useState(false);
  const [selectedGroupForTask, setSelectedGroupForTask] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSuperUser, setIsSuperUser] = useState(false);
  
  // Form state
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const checkSuperUser = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/is-super-user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsSuperUser(data.is_super_user);
      }
    } catch (error) {
      console.error('Error checking super user status:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchGroups();
    fetchUsers();
    checkSuperUser();
  }, [fetchGroups, fetchUsers, checkSuperUser]);

  const resetForm = () => {
    setGroupName('');
    setGroupDescription('');
    setSelectedMembers([]);
    setSearchTerm('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (group) => {
    setSelectedGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    setSelectedMembers(group.members.map(m => m.user_id));
    setShowEditModal(true);
  };

  const handleMemberToggle = (userId) => {
    setSelectedMembers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    try {
      const response = await fetch(`${API}/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: groupName,
          description: groupDescription,
          member_ids: selectedMembers
        })
      });

      if (response.ok) {
        toast.success('Group created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchGroups();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create group');
      }
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    try {
      const response = await fetch(`${API}/groups/${selectedGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: groupName,
          description: groupDescription,
          member_ids: selectedMembers
        })
      });

      if (response.ok) {
        toast.success('Group updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchGroups();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update group');
      }
    } catch (error) {
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Group deleted successfully!');
        fetchGroups();
      } else {
        toast.error('Failed to delete group');
      }
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getMemberNames = (members) => {
    if (members.length === 0) return 'No members';
    if (members.length <= 3) {
      return members.map(m => m.name.split(' ')[0]).join(', ');
    }
    return `${members.slice(0, 3).map(m => m.name.split(' ')[0]).join(', ')} +${members.length - 3} more`;
  };

  const openGroupTaskModal = (group) => {
    setSelectedGroupForTask(group);
    setShowGroupTaskModal(true);
  };

  const handleGroupTaskCreated = () => {
    setShowGroupTaskModal(false);
    setSelectedGroupForTask(null);
    if (onTaskCreated) {
      onTaskCreated();
    }
  };

  return (
    <>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Users className="w-5 h-5" />
          User Groups
        </CardTitle>
        <Button size="sm" onClick={openCreateModal} data-testid="create-group-btn">
          <Plus className="w-4 h-4 mr-1" />
          New Group
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading groups...</div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No groups yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create a group to organize team members</p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => (
              <div
                key={group.id}
                className="p-4 rounded-xl border bg-card transition-all duration-200 hover:bg-muted/50 hover:shadow-md"
                data-testid={`group-card-${group.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">{group.name}</h4>
                      <Badge variant="secondary" className="text-xs">
                        {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                      </Badge>
                    </div>
                    {group.description && (
                      <p className="text-sm text-muted-foreground mb-2">{group.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {getMemberNames(group.members)}
                    </p>
                    {isSuperUser && group.created_by_name && group.created_by !== user?.id && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <span className="text-primary font-medium">Owner:</span> {group.created_by_name}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openGroupTaskModal(group)}
                      title="Assign task to group"
                      data-testid={`assign-task-group-${group.id}`}
                      disabled={group.member_count === 0}
                    >
                      <ClipboardList className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openEditModal(group)}
                      data-testid={`edit-group-${group.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteGroup(group.id)}
                      data-testid={`delete-group-${group.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create/Edit Group Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              {showEditModal ? 'Edit Group' : 'Create New Group'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Group Name */}
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Marketing Team"
                data-testid="group-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Describe the group purpose..."
                rows={2}
              />
            </div>
            
            {/* Member Selection */}
            <div className="space-y-2">
              <Label>Add Members ({selectedMembers.length} selected)</Label>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {/* Selected Members */}
              {selectedMembers.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-muted/30">
                  {selectedMembers.map(memberId => {
                    const user = users.find(u => u.id === memberId);
                    return user ? (
                      <Badge 
                        key={memberId} 
                        variant="secondary"
                        className="flex items-center gap-1 pr-1"
                      >
                        {user.name}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => handleMemberToggle(memberId)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
              
              {/* User List */}
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {filteredUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No users found
                    </p>
                  ) : (
                    filteredUsers.map(user => (
                      <div 
                        key={user.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedMembers.includes(user.id) 
                            ? 'bg-primary/10' 
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => handleMemberToggle(user.id)}
                      >
                        <Checkbox
                          checked={selectedMembers.includes(user.id)}
                          onCheckedChange={() => handleMemberToggle(user.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={showEditModal ? handleUpdateGroup : handleCreateGroup}
              data-testid="save-group-btn"
            >
              {showEditModal ? 'Update Group' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    
    {/* Group Task Assignment Modal */}
    <GroupTaskModal
      open={showGroupTaskModal}
      onClose={() => {
        setShowGroupTaskModal(false);
        setSelectedGroupForTask(null);
      }}
      onTaskCreated={handleGroupTaskCreated}
      preSelectedGroup={selectedGroupForTask}
    />
    </>
  );
};

export default UserGroups;
